import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { navigate, useLocation } from '@reach/router';
import ReactSelect, {
  components,
  type SingleValue,
  type ControlProps,
  type GroupBase,
} from 'react-select';
import { getLanguagesList, getPathWithLanguage, getLanguageFromPath } from '@/utils/localization';
import {
  LanguageSwitcherWrapper,
  StyledControlIconWrapper,
  StyledReactSelect,
} from './LanguageSwitcher.styles';

const Control = ({
  children,
  ...props
}: ControlProps<
  { label: string; value: string },
  boolean,
  GroupBase<{ label: string; value: string }>
>) => (
  <components.Control {...props}>
    <StyledControlIconWrapper>
      <img src="/assets/icon_language.svg" alt="language switcher" />
    </StyledControlIconWrapper>
    {children}
  </components.Control>
);

const LANGUAGE_MAPPING: { [key: string]: string } = {
  en: 'English',
  jp: '日本語 (Japanese)',
  es: 'Español (Spanish)',
  zh: '简体中文 (Simplified Chinese)',
  fr: 'Français (French)',
  de: 'Deutsch (German)',
  ko: '한국인 (Korean)',
};

const selectComponents = { Control };

type LanguageSwitchProps = {
  availableLanguages?: string[];
};

const LanguageSwitcher = ({ availableLanguages = [] }: LanguageSwitchProps) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  const languageFromPath = getLanguageFromPath(pathname);

  const languagesList = useMemo(
    () =>
      availableLanguages.length > 0
        ? getLanguagesList().filter(
            (lang) => availableLanguages.includes(lang) || lang === languageFromPath
          )
        : getLanguagesList(),
    [availableLanguages, languageFromPath]
  );

  const formattedLanguages = languagesList.map((language) => ({
    label: LANGUAGE_MAPPING[language],
    value: language,
  }));

  const currentLanguage = formattedLanguages.find((lang) => lang.value === languageFromPath);

  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  const handleChange = (selectedLanguage: SingleValue<(typeof formattedLanguages)[0]>) => {
    if (selectedLanguage) {
      setSelectedLanguage(selectedLanguage);
      //this makes sure that the language is changed in the i18n instance
      i18n.changeLanguage(selectedLanguage.value);
      const newPath = getPathWithLanguage(pathname, selectedLanguage.value);
      navigate(newPath);
    }
  };

  if (languagesList.length <= 1 && languagesList[0] === currentLanguage?.value) {
    // NOTE: No need to render the switcher if only one language is available and this is the one already selected
    return null;
  }

  return (
    <LanguageSwitcherWrapper>
      <StyledReactSelect>
        <ReactSelect
          options={formattedLanguages}
          name="language-switcher"
          id="language-switcher"
          classNamePrefix="react-select"
          value={selectedLanguage}
          onChange={handleChange}
          menuPlacement="top"
          isSearchable={false}
          aria-label={t('languageSwitcher.select label')}
          components={selectComponents}
          isMulti={false}
        />
      </StyledReactSelect>
    </LanguageSwitcherWrapper>
  );
};

export default LanguageSwitcher;
